import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '$store';
import useAssets from '~hooks/useAssets';
import { setCurrentStep } from '~modules/media-library/controller/thunks/contribute';
import ContributionDone from '~modules/media-library/view/Contribute/ContributionDone/ContributionDone';
import { EditAssetsForm } from '~modules/media-library/view/Contribute/EditAssetsForm/EditAssetsForm';
import SelectAssetType from '~modules/media-library/view/Contribute/SelectAssetType/SelectAssetType';
import SelectFieldsForm from '~modules/media-library/view/Contribute/SelectFieldsForm/SelectFieldsForm';
import { UploadAssetsForm } from '~modules/media-library/view/Contribute/UploadAssetsForm/UploadAssetsForm';
import WizardSidebar from '~modules/media-library/view/Contribute/WizardSidebar/WizardSidebar';
import styles from './EditAssetsWizard.module.css';

interface EditAssetsWizarddProps {
  onClose?: () => void;
}

export const EditAssetsWizard = ({ onClose }: EditAssetsWizarddProps) => {
  const contribute = useSelector((store: RootState | any) => store.contribute);
  const { currentStep, currentAssetId } = contribute;
  const dispatch = useDispatch();
  const { contributionMode, allFiles } = contribute;

  const onStepComplete = useCallback(() => {
    dispatch(setCurrentStep(currentStep + 1));
  }, [currentStep, dispatch]);

  const { getAssetById } = useAssets();
  const asset = getAssetById(currentAssetId);

  const shouldShowSidebar = (step: React.ReactElement) => {
    if (!step) return false;
    return step.type === SelectAssetType || step.type === EditAssetsForm;
  };

  let steps = [];
  if (contributionMode === 'new') {
    steps = [
      <UploadAssetsForm onClose={onClose} key="upload-assets-form" />,
      <SelectAssetType assetId={currentAssetId} onStepComplete={onStepComplete} key="select-asset-type" />,
      <EditAssetsForm assetId={currentAssetId} onStepComplete={onStepComplete} key="edit-assets-form" />,
      <ContributionDone
        onClose={onClose}
        onStepComplete={onStepComplete}
        continueButton={true}
        key="contribution-done"
      />,
      ...(allFiles?.length > 1 ? [<SelectFieldsForm key="select-fieldsform" />] : []),
    ];
  }
  if (contributionMode === 'edit-single') {
    if (asset) {
      if ('draft' === asset.attributes.workflowStatus) {
        steps = [
          <SelectAssetType assetId={currentAssetId} onStepComplete={onStepComplete} key="select-asset-type" />,
          <EditAssetsForm assetId={currentAssetId} onStepComplete={onStepComplete} key="edit-assets-form" />,
          <ContributionDone
            onClose={onClose}
            onStepComplete={onStepComplete}
            continueButton={false}
            key="contribution-done"
          />,
        ];
      } else {
        steps = [
          <EditAssetsForm assetId={currentAssetId} onStepComplete={onStepComplete} key="edit-assets-form" />,
          <ContributionDone key="contribution-done" />,
        ];
      }
    }
  }
  if (contributionMode === 'edit-multi') {
    if (asset) {
      if ('draft' === asset.attributes.workflowStatus) {
        steps = [
          <SelectAssetType assetId={currentAssetId} onStepComplete={onStepComplete} key="select-asset-type" />,
          <EditAssetsForm assetId={currentAssetId} onStepComplete={onStepComplete} key="edit-assets-form" />,
          <ContributionDone
            onClose={onClose}
            onStepComplete={onStepComplete}
            continueButton={true}
            key="contribution-done"
          />,
          <SelectFieldsForm key="select-fieldsform" />,
        ];
      } else {
        steps = [
          <EditAssetsForm assetId={currentAssetId} onStepComplete={onStepComplete} key="edit-assets-form" />,
          <ContributionDone key="contribution-done" />,
        ];
      }
    }
  }
  if (contributionMode === 'replace') {
    steps = [
      <UploadAssetsForm onClose={onClose} key="upload-assets-form" />,
      <ContributionDone
        onClose={onClose}
        onStepComplete={onStepComplete}
        continueButton={false}
        key="contribution-done"
      />,
    ];
  }

  return (
    <div className={styles.editAssetsWizard}>
      <div className="step-indicator ml-3">
        {currentAssetId} - Step {currentStep + 1} out of {steps.length}
      </div>
      <div className={styles.stepsContainer}>
        <div className={styles.stepsContent}>{steps[currentStep]}</div>
        {shouldShowSidebar(steps[currentStep]) && asset && <WizardSidebar asset={asset} />}
      </div>
    </div>
  );
};
